import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEditMode } from './useEditMode';
import { useAuthStore, useAuthTokenStore } from '../store/authStore';
import { useAuth } from './useApi/useAuth';
import { useSetting } from './useApi/useSetting';
import { useTempAlertStore } from '../store/portal/tempAlertStore';
import { useUserPathResolver } from './useUserPathResolver';
import { usePageOwnerIdentityStore } from '../store/pageOwnerIdentityStore';
import { isPageOwnerMatchedToRaw } from '../util/pageOwnerMatch';
import { resolveArchiveAccess } from '../util/archiveAccess';
import { resolveCurrentPage } from '../util/currentPage';
import { useStandardAlertStore } from '../store/portal/alertStore';
import { getAlertMessage } from '../constants/alertMessages';
import { AlertPosition, AlertType } from '../enum/EnumRepository';


export const useArchiveAccess = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { HANDLE: raw } = useParams<{ HANDLE?: string; }>();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);

  const { setEditMode } = useEditMode();
  const { user, isOwner } = useAuth();
  const { setting, fetchSetting } = useSetting();
  const { setUnauthorizedAccessTempAlert } = useTempAlertStore();
  const { enqueueAlert } = useStandardAlertStore();
  const { authToken } = useAuthTokenStore();
  const authHydrated = useAuthStore((state) => state.hasHydrated);

  const handledAccessKeyRef = useRef<string | null>(null);
  const fetchSettingRef = useRef(fetchSetting);
  fetchSettingRef.current = fetchSetting;

  useUserPathResolver(raw);

  const pageOwnerAui = pageOwner?.aui;
  const pageOwnerMatchesRaw = isPageOwnerMatchedToRaw(raw, pageOwner);
  const isAuthReady = authHydrated && !(user && !authToken);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!pageOwnerAui || !pageOwnerMatchesRaw) return;
    if (setting?.aui === pageOwnerAui) return;

    void fetchSettingRef.current(pageOwnerAui);
  }, [isAuthReady, pageOwnerAui, pageOwnerMatchesRaw, setting?.aui]);

  const isAccessReady =
    !!pageOwner &&
    pageOwnerMatchesRaw &&
    !!setting &&
    setting.aui === pageOwner.aui &&
    isAuthReady;

  const accessDecision = isAccessReady
    ? resolveArchiveAccess(
        setting,
        resolveCurrentPage(pathname).page,
        pageOwner.handle,
        isOwner,
      )
    : null;

  const accessKey = accessDecision && pageOwner
    ? [
        pageOwner.aui,
        pathname,
        accessDecision.type,
        accessDecision.type === 'redirect'
          ? accessDecision.path
          : '',
      ].join(':')
    : null;

  useLayoutEffect(() => {
    if (
      !accessDecision ||
      accessDecision.type === 'allow'
    ) {
      handledAccessKeyRef.current = null;
      return;
    }
    if (handledAccessKeyRef.current === accessKey) return;
    handledAccessKeyRef.current = accessKey;

    if (accessDecision.type === 'redirect') {
      navigate(accessDecision.path, { replace: true });
      return;
    }

    if (accessDecision.type === 'empty') {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('ALL_CONTENT_PRIVATE'),
        callBack: () => navigate('/', { replace: true }),
      });
      return;
    }

    setUnauthorizedAccessTempAlert();
    navigate('/', { replace: true });
  }, [accessKey, accessDecision, enqueueAlert, navigate, setUnauthorizedAccessTempAlert]);

  // 아카이브 내 다른 경로로 이동하면 편집 모드를 종료한다.
  useLayoutEffect(() => {
    setEditMode(false);
  }, [pathname, setEditMode]);

  return {
    allowAccess: accessDecision?.type === 'allow',
    isOwner,
  };
};
